import type { Partner } from "@expert/shared-types";

import { type TimelineFeatures, useFeatures } from "@expert/features";
import { type GaiaSubscription, type GaiaWebsocketContext, useGaiaWebsocket } from "@expert/gaia";
import { useFetchOutageInfo } from "@expert/solve-tools";
import { usePrevious } from "@mantine/hooks";
import { truncateAnalyticsText } from "@soluto-private/eventualize-react";
import { useExpertNoteStore } from "@soluto-private/expert-workspace-notepad";
import { removePii } from "@soluto-private/expert-workspace-pii";
import {
    addMessage,
    addSeparatorMessage,
    fetchMessageHistory,
    formatTimestamp,
    getDefaultMessage,
    solveSessionClosed,
    solveSessionStarted,
} from "@soluto-private/expert-workspace-timeline";
import pRetry from "p-retry";
import { useEffect, useState } from "react";

import { sendBusinessEvent } from "../analytics";
import { getEaLogger, useExpertAssistStore } from "../state";

interface SessionLifecycleProps {
    features: Pick<TimelineFeatures, "isProactiveEnabled" | "isReactiveCallContextV2Enabled" | "isSalesFAQEnabled">;
    sessionId: string;
    websocketObj: GaiaWebsocketContext;
    withCustomer: boolean;
    partner: Partner;
    callSid?: string;
    lob?: string;
}

type SubscribeProps = {
    websocketObj: GaiaWebsocketContext;
} & Omit<GaiaSubscription, "sendJsonMessage" | "timeout" | "logger">;

const subscriptionTimeoutDuration = 2000;
const subscriptionRetryCount = 3;

export const useVoiceExpertAssistSessions = () => {
    const { sessionId, callSid, partner, lob, token } = useExpertAssistStore();
    const websocketObj = useGaiaWebsocket();
    const { features } = useFeatures<TimelineFeatures>();
    const loggerWithContext = getEaLogger();
    const { fetchOutageInfo } = useFetchOutageInfo(loggerWithContext);

    const prevSessionId = usePrevious(sessionId);
    const previousCallSid = usePrevious(callSid);
    const previousWSLoading = usePrevious(websocketObj?.loading);
    const [currentSolveSessionId, setCurrentSolveSessionId] = useState("");

    useEffect(() => {
        if (!sessionId || !websocketObj || websocketObj.loading) {
            return;
        }

        const sessionIdChanged = prevSessionId !== sessionId;
        const websocketObjJustLoaded = previousWSLoading === true;
        const withCustomer = !!callSid;

        if (sessionIdChanged && prevSessionId && !withCustomer) {
            addSeparatorMessage(prevSessionId, {
                text: "Call ended",
                timestamp: formatTimestamp(),
            });
            addMessage(sessionId, {
                ...getDefaultMessage("offCall"),
                id: `offCall_${crypto.randomUUID()}`,
                timestamp: formatTimestamp(),
                isUnread: true,
            });
        }

        if (sessionIdChanged && prevSessionId) {
            closeSession({ sessionId: prevSessionId, callSid: previousCallSid, websocketObj, partner, features });
            setCurrentSolveSessionId(sessionId);
            void startSession({ sessionId, callSid, partner, websocketObj, withCustomer: !!callSid, features, lob });
            return;
        }

        if (websocketObjJustLoaded || (sessionIdChanged && !prevSessionId)) {
            if (currentSolveSessionId === sessionId) {
                void subscribeSessionToGaia({ sessionId, websocketObj, callSid, partner });
            } else {
                setCurrentSolveSessionId(sessionId);
                void startSession({
                    partner,
                    sessionId,
                    websocketObj,
                    callSid,
                    withCustomer,
                    features,
                    lob,
                });
            }
        }
        // oxlint-disable-next-line exhaustive-deps
    }, [callSid, sessionId, websocketObj?.loading]);

    useEffect(() => {
        if (sessionId && token) {
            void fetchOutageInfo(token);
        }
        // oxlint-disable-next-line exhaustive-deps
    }, [sessionId, token]);
};

const startSession = async ({
    sessionId,
    websocketObj,
    withCustomer,
    callSid,
    partner,
    features,
    lob,
}: SessionLifecycleProps) => {
    const loggerWithContext = getEaLogger();
    loggerWithContext.info({ module: "useVoiceExpertAssistSessions" }, "Expert Assist Session | Started");
    solveSessionStarted({ sessionId, callSid, withCustomer, partner, features });
    await subscribeSessionToGaia({ sessionId, websocketObj, callSid, partner });
    fetchMessageHistory({
        sessionId,
        sendJsonMessage: websocketObj.sendJsonMessage,
        partner,
        lob,
    });
};

const subscribeSessionToGaia = async ({ sessionId, callSid, partner, websocketObj }: SubscribeProps) => {
    const loggerWithContext = getEaLogger();
    try {
        await pRetry(
            async () => {
                await websocketObj.subscribeSessionToGaia({
                    sessionId,
                    callSid,
                    partner,
                    sendJsonMessage: websocketObj.sendJsonMessage,
                    timeout: subscriptionTimeoutDuration,
                    logger: loggerWithContext,
                });
            },
            { minTimeout: subscriptionTimeoutDuration, factor: 1, retries: subscriptionRetryCount },
        );
    } catch (error) {
        loggerWithContext.error(
            { error, module: "useVoiceExpertAssistSessions", sessionId, callSid, subscriptionRetryCount },
            "Error subscribing session to gaia with max retries",
        );
    }
};

const closeSession = ({
    callSid,
    features,
    partner,
    sessionId,
    websocketObj,
}: Omit<SessionLifecycleProps, "withCustomer">) => {
    const loggerWithContext = getEaLogger();
    const { note } = useExpertNoteStore.getState();
    if (note) {
        void removePii(note).then((piiScrubbedNote) => {
            sendBusinessEvent({
                eventName: "SaveExpertNote",
                identities: { SessionId: sessionId },
                meta: { SkipMixpanel: true },
                attributes: { note: truncateAnalyticsText(piiScrubbedNote) },
            });
        });
    }
    loggerWithContext.info({ module: "useVoiceExpertAssistSessions" }, "Expert Assist Session | Closed");
    solveSessionClosed({ sessionId, callSid, partner, features });
    websocketObj.unsubscribeSessionFromGaia({
        sessionId,
        sendJsonMessage: websocketObj.sendJsonMessage,
        logger: loggerWithContext,
    });
};
